import { useEffect, useRef } from 'react'
import './reticle.css'

/**
 * Der Zeiger als Instrument.
 *
 * Statt eines zweiten Mauszeigers läuft ein Fadenkreuz mit — träge, mit
 * Nachlauf — und rastet auf Bedienelementen ein: dort wird aus dem Kreis ein
 * Rechteck, das die Schaltfläche fasst. Das ist die Geste, die in den
 * Vorlagen den Eindruck macht, das Bild sei ein Sichtgerät und keine Seite.
 *
 * Vier Dinge, die es tragfähig statt spielerisch machen:
 *
 *  · Der echte Zeiger bleibt sichtbar. Ihn zu verstecken heisst, sich auf ein
 *    Element zu verlassen, das bei jedem Ruckler nachhinkt — auf einer Seite
 *    mit Formularen ist das kein Effekt mehr, sondern ein Bedienfehler.
 *  · Alles läuft in EINEM rAF und schreibt direkt in den Stil des Knotens.
 *    Über React-State wäre das ein Rerender je Bild.
 *  · Geräte ohne feinen Zeiger bekommen es gar nicht erst. Auf einem Tablet
 *    hätte ein Mauszeiger-Nachlauf keine Bedeutung.
 *  · Bei `prefers-reduced-motion` bleibt es aus.
 */

/** Was als Ziel gilt, auf das eingerastet wird. */
const TARGETS = 'a[href], button, [role="button"], input, select, textarea, summary'

export function Reticle() {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fine = window.matchMedia('(pointer: fine)').matches
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!fine || reduced) return

    const node = ref.current
    if (!node) return

    // Zielzustand (worauf zugelaufen wird) und Istzustand (was gezeichnet ist).
    const goal = { x: innerWidth / 2, y: innerHeight / 2, w: 34, h: 34, r: 999, on: 0 }
    const now = { ...goal }
    let raf = 0
    let visible = false

    const onMove = (event: PointerEvent) => {
      if (!visible) {
        // Beim ersten Auftauchen NICHT von der Bildmitte heranfliegen —
        // das sieht aus, als würde etwas geworfen.
        now.x = event.clientX
        now.y = event.clientY
        visible = true
        node.style.opacity = '1'
      }

      const target = (event.target as Element | null)?.closest?.(TARGETS) ?? null

      if (target) {
        const box = target.getBoundingClientRect()
        // Auf das Element einrasten: Mitte, Grösse, Eckenradius übernehmen.
        goal.x = box.left + box.width / 2
        goal.y = box.top + box.height / 2
        goal.w = box.width + 12
        goal.h = box.height + 12
        goal.r = parseFloat(getComputedStyle(target).borderRadius) || 8
        goal.on = 1
      } else {
        goal.x = event.clientX
        goal.y = event.clientY
        goal.w = 34
        goal.h = 34
        goal.r = 999
        goal.on = 0
      }
    }

    const onLeave = () => {
      visible = false
      node.style.opacity = '0'
    }

    const draw = () => {
      // Zwei Geschwindigkeiten: die Position folgt schnell, Grösse und Form
      // langsamer. Dadurch wirkt das Einrasten wie ein Mechanismus statt wie
      // ein Sprung.
      now.x += (goal.x - now.x) * 0.24
      now.y += (goal.y - now.y) * 0.24
      now.w += (goal.w - now.w) * 0.16
      now.h += (goal.h - now.h) * 0.16
      now.r += (goal.r - now.r) * 0.16
      now.on += (goal.on - now.on) * 0.16

      node.style.transform = `translate3d(${(now.x - now.w / 2).toFixed(1)}px, ${(
        now.y -
        now.h / 2
      ).toFixed(1)}px, 0)`
      node.style.width = `${now.w.toFixed(1)}px`
      node.style.height = `${now.h.toFixed(1)}px`
      node.style.borderRadius = `${now.r.toFixed(1)}px`
      node.style.setProperty('--reticle-lock', now.on.toFixed(3))

      raf = requestAnimationFrame(draw)
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    document.addEventListener('pointerleave', onLeave)
    raf = requestAnimationFrame(draw)

    return () => {
      window.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerleave', onLeave)
      cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <div className="reticle" ref={ref} aria-hidden="true">
      <span className="reticle__tick reticle__tick--n" />
      <span className="reticle__tick reticle__tick--s" />
      <span className="reticle__tick reticle__tick--w" />
      <span className="reticle__tick reticle__tick--e" />
    </div>
  )
}
